import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, Scene } from "./types";

// Lesson: Reports from audio → Transcription. Fixture `transcriptionAndSpeakers`:
// the Valley Mart interview (two voices, interaction audio) with one misheard
// word ("licker") and unnamed speakers. Segment ids: 1 = the line with the
// misheard word (Speaker 2), 3 = "No. He walked right past ..." (split/merge).
const CASE_ID = "c0000000-0000-4000-8000-000000000301";
const seg = (id: number) => `[data-segment-id="${id}"]`;

const scene: Scene = {
  id: "transcription-and-speakers",
  title: "Transcription",
  startRoute: `/cases/${CASE_ID}`,
  fixture: "transcriptionAndSpeakers",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "Every case keeps its transcript, synced to the audio. Open the Transcription tab and you see each line, with who said it and when.",
      actions: [
        // "Open the Transcription tab" starts at 3.7 s of 8.1 s.
        { type: "waitFor", target: sel("editorTabTranscription") },
        { type: "wait", ms: 3300 },
        { type: "click", target: sel("editorTabTranscription") },
        { type: "waitFor", target: seg(1) },
        { type: "wait", ms: 2500 },
      ],
    },
    {
      id: "follow",
      narration:
        "Click any line and the audio plays from there. Turn on Follow audio, and the current line highlights as the recording plays.",
      actions: [
        // "Click any line" at 0 s, "Turn on Follow audio" at 3.0 s.
        { type: "wait", ms: 300 },
        { type: "click", target: `${seg(0)} ${sel("transcriptSegment")}` },
        { type: "wait", ms: 1500 },
        { type: "click", target: sel("transcriptFollow") },
        { type: "wait", ms: 2500 },
      ],
    },
    {
      id: "fix",
      narration:
        "If the engine misheard a word, open the line's menu and choose Edit Transcription. Here it heard licker instead of liquor, so I'll retype the line and save.",
      actions: [
        // Menu opens at ~2 s, "Edit Transcription" at ~4.3 s, retype from 7.9 s.
        { type: "moveTo", target: `${seg(1)} ${sel("transcriptSegment")}` },
        { type: "wait", ms: 800 },
        { type: "click", target: `${seg(1)} ${sel("transcriptSegmentMenu")}` },
        { type: "wait", ms: 1000 },
        { type: "click", target: `${seg(1)} ${sel("transcriptSegmentEdit")}` },
        { type: "wait", ms: 1900 },
        {
          type: "typeOver",
          target: `${seg(1)} textarea`,
          text: "I saw him put two bottles of liquor in a black backpack.",
          cps: 22,
        },
        { type: "wait", ms: 300 },
        { type: "click", target: sel("transcriptSegmentSave") },
        { type: "wait", ms: 1200 },
      ],
    },
    {
      id: "speakers",
      narration:
        "Next, the speakers. Open a speaker's name and click the pencil beside it, and the Edit Speaker Name box opens. I'll call this speaker Daniel Reyes.",
      actions: [
        // Name menu ~2 s, pencil ~3.8 s, box opens ~4.8 s, typing from 7.3 s.
        { type: "wait", ms: 1000 },
        { type: "click", target: `${seg(1)} ${sel("transcriptSpeaker")}` },
        { type: "wait", ms: 700 },
        { type: "click", target: `${seg(1)} li:nth-child(2) ${sel("transcriptSpeakerEdit")}` },
        { type: "waitFor", target: `${sel("transcriptSpeakerDialog")} input` },
        { type: "wait", ms: 1500 },
        { type: "typeOver", target: `${sel("transcriptSpeakerDialog")} input`, text: "Daniel Reyes", cps: 10 },
        { type: "wait", ms: 400 },
        { type: "click", target: sel("transcriptSpeakerSave") },
        { type: "wait", ms: 1000 },
      ],
    },
    {
      id: "split",
      narration:
        "A line can also be split or merged. Choose Split Segment, click between two words, and the line becomes two. Merge with Next puts them back together.",
      actions: [
        // Split Segment at 2.9 s, "click between two words" 4.4 s, "Merge with Next" 7.4 s.
        { type: "scrollIntoView", target: seg(3) },
        { type: "moveTo", target: `${seg(3)} ${sel("transcriptSegment")}` },
        { type: "wait", ms: 900 },
        { type: "click", target: `${seg(3)} ${sel("transcriptSegmentMenu")}` },
        { type: "wait", ms: 400 },
        { type: "click", target: `${seg(3)} ${sel("transcriptSegmentSplit")}` },
        { type: "wait", ms: 900 },
        { type: "click", target: `${seg(3)} ${sel("transcriptSplitPoint")}` },
        { type: "wait", ms: 1800 },
        { type: "click", target: `${seg(3)} ${sel("transcriptSegmentMenu")}` },
        { type: "wait", ms: 300 },
        { type: "click", target: `${seg(3)} ${sel("transcriptSegmentMergeNext")}` },
        { type: "wait", ms: 1200 },
      ],
    },
    {
      id: "outro",
      narration:
        "When the transcript reads right, go back to the Narrative tab and regenerate. Do the naming first, so the new draft says Daniel Reyes instead of Speaker 2.",
      actions: [
        // "go back to the Narrative tab" at 1.9 s.
        { type: "wait", ms: 1300 },
        { type: "click", target: sel("editorTabNarrative") },
        { type: "waitFor", target: sel("editorNarrative") },
        { type: "wait", ms: 4500 },
      ],
    },
  ],
};

export default scene;
